import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  UserBookingVisaLetterPrintableDocument,
  UserBookingVisaLetterPrintableQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import countries from '../../../dataObjects/countries';

/**
 * Embassy Letter To
 */
export function LetterTo() {
  const { watch, register } = useForm({
    defaultValues: {
      name: 'Immigration Officer',
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
    },
  });

  return (
    <>
      <Col xs={12} sm={6} className="d-print-none">
        <strong>To:</strong>
        <Form>
          <FloatingLabel controlId="name" label="Name" className="mb-3">
            <Form.Control {...register('name')} />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Embassy Address Line 1"
            className="mb-3"
          >
            <Form.Control {...register('line1')} />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Embassy Address Line 2"
            className="mb-3"
          >
            <Form.Control {...register('line2')} />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Embassy Address Line 3"
            className="mb-3"
          >
            <Form.Control {...register('line3')} />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Embassy Address Line 4"
            className="mb-3"
          >
            <Form.Control {...register('line4')} />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Embassy Country"
            className="mb-3"
          >
            <Form.Control {...register('line5')} />
          </FloatingLabel>
        </Form>
      </Col>

      <Col xs={6} className="d-none d-print-block">
        <strong>To:</strong>
        <p>
          {watch('name')}
          {watch('line1') !== '' && (
            <>
              <br />
              {watch('line1')}
            </>
          )}
          {watch('line2') !== '' && (
            <>
              <br />
              {watch('line2')}
            </>
          )}
          {watch('line3') !== '' && (
            <>
              <br />
              {watch('line3')}
            </>
          )}{' '}
          {watch('line4') !== '' && (
            <>
              <br />
              {watch('line4')}
            </>
          )}{' '}
          {watch('line5') !== '' && (
            <>
              <br />
              {watch('line5')}
            </>
          )}
        </p>
      </Col>
    </>
  );
}

/**
 * Letter Contents
 */
export function LetterContents({
  booking,
}: {
  booking: UserBookingVisaLetterPrintableQuery['booking'];
}) {
  const { watch, register } = useForm({
    defaultValues: {
      name: `${booking.user.givenName} ${booking.user.familyName}`,
    },
  });

  const name = watch('name');

  return (
    <>
      <Row className="py-4">
        <Col xs={12} className="d-print-none">
          <p>
            <strong>Letter:</strong>
          </p>
          <Form>
            <FloatingLabel
              controlId="name"
              label="Your full name (as on your passport)"
              className="mb-3"
            >
              <Form.Control
                {...register('name')}
                autoComplete="name"
                required
              />
            </FloatingLabel>
          </Form>
        </Col>

        <Col xs={12}>
          <p>Re: Invitation Letter for {name}.</p>

          <p>Dear Sir/Madam,</p>

          <p>
            We, as a registered travel company (tour operator), are writing this
            letter to support the visa application of {name}. Our company
            trading name is {booking.trip.host.name}.
          </p>

          <p>
            {name} is joining us on an all-inclusive tour, including
            accommodation, called {booking.trip.title}. The customer has already
            paid in full for this trip. Their trip starts on{' '}
            {DateTime.fromISO(booking.group.start.utc, {
              zone: booking.group.start.zone,
            }).toFormat('d LLLL yyyy')}{' '}
            and finishes on{' '}
            {DateTime.fromISO(booking.group.end.utc, {
              zone: booking.group.end.zone,
            }).toFormat('d LLLL yyyy')}
            .
          </p>

          <p>If you have any questions, please feel free to contact me.</p>

          <p>
            Kind regards,
            <br />
            {booking.trip.host.contact.contactName || booking.trip.host.name}
          </p>
        </Col>
      </Row>
    </>
  );
}

export default function VisaLetterPrintable({ query }) {
  const { data, loading, error } = useQuery(
    UserBookingVisaLetterPrintableDocument,
    {
      variables: { id: query.bookingId },
      fetchPolicy: 'cache-and-network',
    },
  );

  // Handle loading/errors
  if (!data && loading) return <LoadingPage />;
  if (!data && error) return <ErrorPage error={error} />;

  const booking = data?.booking;
  const { host } = booking.trip;
  const hostAddress = host.businessAddress!;

  return (
    <Container className="mt-4">
      <Breadcrumb
        listProps={{ className: 'ps-0 justify-content-start' }}
        className="d-print-none"
      >
        <Link href="/" passHref legacyBehavior>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
        </Link>
        <Link href={`/me/${booking.user.id}`} passHref legacyBehavior>
          <Breadcrumb.Item>Your dashboard</Breadcrumb.Item>
        </Link>
        <Link
          href={`/me/${booking.user.id}/bookings/${booking.id}`}
          passHref
          legacyBehavior
        >
          <Breadcrumb.Item>Booking {booking.id}</Breadcrumb.Item>
        </Link>
        <Breadcrumb.Item active>Invoice</Breadcrumb.Item>
      </Breadcrumb>
      <h1 className="d-print-none">Create your printable visa letter</h1>

      <Alert variant="warning" className="my-3 d-print-none">
        <Alert.Heading>
          Please check this is sufficient for your specific visa
        </Alert.Heading>
        <p>
          This letter is created automatically by Skyhook, on behalf of your
          local guides.
        </p>
        <p>
          Skyhook cannot guarantee that this letter contains everything you need
          for the visa you are applying for. Please do check it contains
          everything you need, for the specific visa you are applying for.
        </p>
      </Alert>

      <p>
        <strong>Date:</strong>{' '}
        {DateTime.fromISO(booking.created, { zone: 'utc' }).toLocaleString(
          DateTime.DATE_FULL,
        )}
      </p>

      <Row>
        <LetterTo />

        <Col>
          <strong>From:</strong>
          <p>
            {host.legalName}
            {hostAddress.addressLine1 && (
              <>
                <br />
                {hostAddress.addressLine1}
              </>
            )}
            {hostAddress.addressLine2 && (
              <>
                <br />
                {hostAddress.addressLine2}
              </>
            )}
            {hostAddress.postalCode && (
              <>
                <br />
                {hostAddress.postalCode}
              </>
            )}
            {hostAddress.sortingCode && (
              <>
                <br />
                {hostAddress.sortingCode}
              </>
            )}
            {hostAddress.dependentLocality && (
              <>
                <br />
                {hostAddress.dependentLocality}
              </>
            )}
            {hostAddress.locality && (
              <>
                <br />
                {hostAddress.locality}
              </>
            )}
            {hostAddress.administrativeArea && (
              <>
                <br />
                {hostAddress.administrativeArea}
              </>
            )}
            <br />
            {countries[hostAddress.country]}
            <br />
            {booking.trip.host.contact.phone || ''}
            <br />
            {booking.trip.host.contact.email || ''}
          </p>
        </Col>
      </Row>

      <LetterContents booking={booking} />

      <div className="d-grid gap-2 d-print-none">
        <Button onClick={() => window.print()} variant="primary" size="lg">
          Print
        </Button>
      </div>
    </Container>
  );
}
